import type { SolicitacaoDepartamento, SolicitacaoTipo } from '../../types';

// Fonte única do vocabulário de tipo_solicitacao — o mapa rótulo+cor vivia duplicado
// literalmente em TarefasView.tsx e SolicitacaoDetailModal.tsx. Fica num módulo de constantes
// (e não na TarefasView) pra não acoplar o modal à view.
// A ordem daqui é a ordem exibida nos selects; os valores espelham o CHECK
// tarefas_tipo_solicitacao_check (migração 20260807120000).
//
// Cada tipo pertence a um departamento (Tecnologia ou Marketing) — o formulário de criação
// filtra a lista de tipos pelo departamento escolhido (ver gruposDoDepartamento/tiposDoGrupo).
// "Apoio" é um nome de grupo compartilhado pelos dois departamentos, mas com itens distintos.

export const DEPARTAMENTOS_SOLICITACAO: SolicitacaoDepartamento[] = ['Tecnologia', 'Marketing', 'Financeiro', 'Faturamento', 'Comercial'];

export const DEPARTAMENTO_BADGE_CLASS: Record<SolicitacaoDepartamento, string> = {
  Tecnologia: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
  Marketing: 'bg-pink-50 text-pink-600 dark:bg-pink-900/20 dark:text-pink-400',
  Financeiro: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  Faturamento: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400',
  Comercial: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
};

// Equipes da Central (#6518, decidido com o Victor em 29/10/2026). Uma equipe recebe as
// solicitações dos seus `departamentos` e é formada por quem tem um dos `membros` no cadastro
// (profiles.departamento ou profiles.departamentos — src/lib/departamentos.ts). Quem não é
// adm/gerente vê as próprias solicitações + a fila sem dono das suas equipes; adm/gerente vê tudo.
// `avisar` = líderes que recebem aviso no sino quando chega pedido novo. Tecnologia e Marketing
// não avisam ninguém novo: a fila delas segue como antes.
export interface EquipeSolicitacao {
  id: string;
  label: string;
  departamentos: SolicitacaoDepartamento[];
  membros: string[];
  avisar: string[];
}

export const EQUIPES_SOLICITACAO: EquipeSolicitacao[] = [
  { id: 'tecnologia_marketing', label: 'Tecnologia e Marketing', departamentos: ['Tecnologia', 'Marketing'], membros: ['Tecnologia', 'Marketing'], avisar: [] },
  { id: 'financeiro', label: 'Financeiro', departamentos: ['Financeiro'], membros: ['Financeiro', 'Pós Venda', 'Suporte e Atendimento'],
    avisar: ['d7ce4209-0f7b-4e88-ade8-abbf70e2c5f7'] }, // Meyryene Peixoto
  { id: 'faturamento', label: 'Faturamento', departamentos: ['Faturamento'], membros: ['Faturamento'],
    avisar: ['a40f054d-99d8-451d-a137-60485581fb27'] }, // Vinicius Alves
  { id: 'comercial', label: 'Comercial', departamentos: ['Comercial'], membros: ['Comercial'], // cadastro unificou Interno e Externo em 'Comercial' (29/10/2026); Parcerias fica de fora
    avisar: ['f275c5af-2a2f-4209-bee5-c57eb1b64bb3'] }, // Talita Bastos (Lucília Araújo saiu: inativa, 29/10/2026)
];

/** Equipe dona de um departamento de solicitação (null = solicitação antiga sem departamento). */
export const equipeDoDepartamento = (departamento?: SolicitacaoDepartamento | null): EquipeSolicitacao | null =>
  EQUIPES_SOLICITACAO.find(e => !!departamento && e.departamentos.includes(departamento)) ?? null;

/** Só Tecnologia e Marketing têm tipo; as outras equipes pedem só título, descrição e prioridade. */
export const departamentoTemTipos = (departamento?: SolicitacaoDepartamento | null): boolean =>
  departamento === 'Tecnologia' || departamento === 'Marketing';

/** Equipes de uma pessoa pelo cadastro (departamento principal + os demais marcados). */
export const equipesDoPerfil = (perfil?: { departamento?: string | null; departamentos?: string[] | null } | null): EquipeSolicitacao[] => {
  const deps = new Set([perfil?.departamento, ...(perfil?.departamentos || [])].filter(Boolean) as string[]);
  return EQUIPES_SOLICITACAO.filter(e => e.membros.some(m => deps.has(m)));
};

/** Quem vê tudo na Central: adm e gerentes (os líderes de área). */
export const veTodasAsSolicitacoes = (role?: string | null): boolean =>
  role === 'adm' || role === 'gerente' || role === 'gerente_parcerias';

export type SolicitacaoTipoGrupo =
  // Tecnologia
  | 'Problema' | 'Mudança' | 'Apoio' | 'Infra & Integração'
  // Marketing (Apoio é reaproveitado do grupo acima, com itens próprios)
  | 'Criação' | 'Divulgação';

export interface SolicitacaoTipoDef {
  value: SolicitacaoTipo;
  label: string;
  grupo: SolicitacaoTipoGrupo;
  departamento: SolicitacaoDepartamento;
  className: string;
  /** Definição curta usada no prompt da transcrição por áudio e como title no select. */
  descricao: string;
}

export const SOLICITACAO_TIPOS: SolicitacaoTipoDef[] = [
  // ── Tecnologia ──────────────────────────────────────────────────────
  {
    value: 'bug',
    label: 'Bug',
    grupo: 'Problema',
    departamento: 'Tecnologia',
    className: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
    descricao: 'algo quebrado, com erro ou comportamento errado no sistema',
  },
  {
    value: 'correcao_dados',
    label: 'Correção de dados',
    grupo: 'Problema',
    departamento: 'Tecnologia',
    className: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
    descricao: 'um registro está com informação errada e precisa ser corrigido (valor, vínculo, status)',
  },
  {
    value: 'melhoria',
    label: 'Melhoria',
    grupo: 'Mudança',
    departamento: 'Tecnologia',
    className: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    descricao: 'algo que já existe e funciona, mas pode ficar melhor',
  },
  {
    value: 'edicao_cadastro',
    label: 'Edição / Ajuste de cadastro',
    grupo: 'Mudança',
    departamento: 'Tecnologia',
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
    descricao: 'alterar um cadastro, campo, texto ou configuração do sistema',
  },
  {
    value: 'sugestao',
    label: 'Sugestão / Nova funcionalidade',
    grupo: 'Mudança',
    departamento: 'Tecnologia',
    className: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
    descricao: 'uma ideia nova / funcionalidade que ainda não existe',
  },
  {
    value: 'duvida_suporte',
    label: 'Dúvida / Suporte',
    grupo: 'Apoio',
    departamento: 'Tecnologia',
    className: 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400',
    descricao: 'não sei usar, não sei onde fica, preciso de ajuda para fazer algo',
  },
  {
    value: 'acesso_permissao',
    label: 'Acesso / Permissão',
    grupo: 'Apoio',
    departamento: 'Tecnologia',
    className: 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400',
    descricao: 'liberar usuário, papel, senha ou permissão de tela',
  },
  {
    value: 'relatorio_exportacao',
    label: 'Relatório / Exportação',
    grupo: 'Apoio',
    departamento: 'Tecnologia',
    className: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    descricao: 'preciso de um extrato, planilha, dashboard ou exportação de dados',
  },
  {
    value: 'integracao',
    label: 'Integração',
    grupo: 'Infra & Integração',
    departamento: 'Tecnologia',
    className: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
    descricao: 'n8n, API externa, webhook, concessionária, sistema de terceiro',
  },
  {
    value: 'infraestrutura',
    label: 'Infraestrutura / Equipamento',
    grupo: 'Infra & Integração',
    departamento: 'Tecnologia',
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300',
    descricao: 'máquina, impressora, rede, e-mail, licença — TI física, fora do sistema',
  },
  {
    value: 'outro_tecnologia',
    label: 'Outro',
    grupo: 'Apoio',
    departamento: 'Tecnologia',
    className: 'bg-stone-100 text-stone-600 dark:bg-stone-700/40 dark:text-stone-300',
    descricao: 'pedido de Tecnologia que não se encaixa em nenhuma opção acima',
  },

  // ── Marketing ───────────────────────────────────────────────────────
  {
    value: 'peca_grafica',
    label: 'Peça gráfica / Arte',
    grupo: 'Criação',
    departamento: 'Marketing',
    className: 'bg-pink-50 text-pink-600 dark:bg-pink-900/20 dark:text-pink-400',
    descricao: 'arte, banner, post estático, material impresso',
  },
  {
    value: 'video_reels',
    label: 'Vídeo / Reels',
    grupo: 'Criação',
    departamento: 'Marketing',
    className: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400',
    descricao: 'vídeo, reels, edição, motion',
  },
  {
    value: 'copywriting',
    label: 'Copywriting / Texto',
    grupo: 'Criação',
    departamento: 'Marketing',
    className: 'bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-900/20 dark:text-fuchsia-400',
    descricao: 'texto para post, e-mail, roteiro, legenda',
  },
  {
    value: 'campanha_anuncio',
    label: 'Campanha / Anúncio pago',
    grupo: 'Divulgação',
    departamento: 'Marketing',
    className: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
    descricao: 'campanha paga, tráfego pago, impulsionamento',
  },
  {
    value: 'post_social',
    label: 'Post para redes sociais',
    grupo: 'Divulgação',
    departamento: 'Marketing',
    className: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/20 dark:text-cyan-400',
    descricao: 'publicação orgânica em Instagram, Facebook, LinkedIn...',
  },
  {
    value: 'landing_page',
    label: 'Landing page / Site',
    grupo: 'Divulgação',
    departamento: 'Marketing',
    className: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
    descricao: 'página de captura, site, formulário',
  },
  {
    value: 'relatorio_metricas',
    label: 'Relatório / Métricas',
    grupo: 'Apoio',
    departamento: 'Marketing',
    className: 'bg-lime-50 text-lime-700 dark:bg-lime-900/20 dark:text-lime-400',
    descricao: 'desempenho de campanha, alcance, engajamento',
  },
  {
    value: 'outro',
    label: 'Outro',
    grupo: 'Apoio',
    departamento: 'Marketing',
    className: 'bg-stone-100 text-stone-600 dark:bg-stone-700/40 dark:text-stone-300',
    descricao: 'não se encaixa em nenhuma opção acima',
  },
];

// Lista "achatada" com os grupos na ordem de exibição, sem duplicar "Apoio" (compartilhado).
export const SOLICITACAO_TIPO_GRUPOS: SolicitacaoTipoGrupo[] = [
  'Problema', 'Mudança', 'Apoio', 'Infra & Integração', 'Criação', 'Divulgação',
];

export const SOLICITACAO_TIPO_VALUES = SOLICITACAO_TIPOS.map(t => t.value);

/** Grupos que existem dentro de um departamento específico, na ordem certa pro select da criação. */
export const gruposDoDepartamento = (departamento: SolicitacaoDepartamento): SolicitacaoTipoGrupo[] =>
  SOLICITACAO_TIPO_GRUPOS.filter(grupo => SOLICITACAO_TIPOS.some(t => t.grupo === grupo && t.departamento === departamento));

/** Sem `departamento`, devolve os tipos do grupo nos dois departamentos juntos (uso do filtro geral). */
export const tiposDoGrupo = (grupo: SolicitacaoTipoGrupo, departamento?: SolicitacaoDepartamento) =>
  SOLICITACAO_TIPOS.filter(t => t.grupo === grupo && (!departamento || t.departamento === departamento));

/** Rótulo + classes do badge. Devolve null quando não há tipo (badge some). */
export const tipoSolicitacaoInfo = (tipo?: SolicitacaoTipo | null): { label: string; className: string } | null => {
  const def = SOLICITACAO_TIPOS.find(t => t.value === tipo);
  return def ? { label: def.label, className: def.className } : null;
};

/** Departamento dono de um tipo — usado pra auto-preencher o campo Departamento após a
 *  transcrição por áudio sugerir um tipo (ver TaskModal.tsx). */
export const departamentoDoTipo = (tipo?: SolicitacaoTipo | null): SolicitacaoDepartamento | null => {
  const def = SOLICITACAO_TIPOS.find(t => t.value === tipo);
  return def?.departamento ?? null;
};

/** true pros dois "Outro" (Tecnologia e Marketing) — é quando o campo de texto livre
 *  tipo_outro_detalhe aparece, já que o CHECK fechado de tipo_solicitacao não classifica. */
export const isTipoOutro = (tipo?: SolicitacaoTipo | null): boolean =>
  tipo === 'outro' || tipo === 'outro_tecnologia';

/** Quem vê o quê na Central (#6518, só na tela — o banco segue liberando a equipe interna):
 *  adm e gerentes veem tudo; os demais veem o que abriram, o que está com eles e a fila sem
 *  dono das suas equipes. Solicitação antiga sem departamento continua visível pra todos. */
export const solicitacaoVisivel = (
  t: { solicitante_id?: string | null; responsavel_id?: string | null; departamento_solicitacao?: SolicitacaoDepartamento | null },
  perfil: { role?: string | null; departamento?: string | null; departamentos?: string[] | null } | null | undefined,
  userId: string | undefined,
): boolean => {
  if (veTodasAsSolicitacoes(perfil?.role)) return true;
  if (!!userId && (t.solicitante_id === userId || t.responsavel_id === userId)) return true;
  const equipe = equipeDoDepartamento(t.departamento_solicitacao);
  if (!equipe) return true;
  // Financeiro, Faturamento e Comercial: todo mundo vê todas (#6557). A regra abaixo fica para TI e Marketing.
  if (equipe.id !== 'tecnologia_marketing') return true;
  return !t.responsavel_id && equipesDoPerfil(perfil).some(e => e.id === equipe.id);
};

// Etapas (#6518): Tecnologia e Marketing seguem com o fluxo de desenvolvimento; as outras equipes
// trabalham por tarefa (Novo → Em andamento → Concluído, ou Recusado). Reaproveita os mesmos
// valores de solicitacao_status — só muda o nome mostrado —, então não há etapa nova no banco.
export const ETAPAS_TI = ['novo', 'em_analise', 'em_desenvolvimento', 'em_teste', 'concluido', 'rejeitado'] as const;
export const ETAPAS_EQUIPE = ['novo', 'em_desenvolvimento', 'concluido', 'rejeitado'] as const;

const ROTULO_ETAPA_TI: Record<string, string> = {
  novo: 'Novo', em_analise: 'Em Análise', em_desenvolvimento: 'Em Desenvolvimento',
  em_teste: 'Em Teste', concluido: 'Concluído', rejeitado: 'Rejeitado',
};
const ROTULO_ETAPA_EQUIPE: Record<string, string> = {
  ...ROTULO_ETAPA_TI, em_analise: 'Em andamento', em_desenvolvimento: 'Em andamento', em_teste: 'Em andamento', rejeitado: 'Recusado',
};

/** Etapas oferecidas para a solicitação (sem departamento = fluxo da TI, como antes). */
export const etapasDoDepartamento = (departamento?: SolicitacaoDepartamento | null): readonly string[] =>
  !departamento || departamentoTemTipos(departamento) ? ETAPAS_TI : ETAPAS_EQUIPE;

/** Nome da etapa como a equipe da solicitação chama. */
export const rotuloEtapa = (status: string | null | undefined, departamento?: SolicitacaoDepartamento | null): string => {
  const s = status || 'novo';
  return (!departamento || departamentoTemTipos(departamento) ? ROTULO_ETAPA_TI : ROTULO_ETAPA_EQUIPE)[s] || s;
};
